import {computed} from "mobx";
import {StandingsRowDomainModel} from "../../../domain/standings/models/row/standings.row-domain-model";
import {BaseViewModel} from "../../../infrastructure/models/base-view-model";
import {StandingWidgetCompetitorViewModel} from "./standings-widget.competitor-view-model";
import {STANDINGS_WIDGET} from "../standings-widget.consts"
import {StandingWidgetLiveGameViewModel} from "./standings-widget.live-game-view-model";
import {TREND} from "../../../core/app/consts";
import {StandingsDestinationViewModel} from "../../../domain/standings/models/destinations/standings.destinations.view-model";
import {StandingsHeaderViewModel} from "../../../domain/standings/models/header/standings-header.view-model";
import {StandingsWidgetCellViewModel} from "./standings-widget.cell-view-model";
import {PartialGameDomainModel} from "../../../domain/partial-game/models/partial-game-domain-model";
import {StandingsWidgetRecentFormGameViewModel} from "./standings-widget.recent-form-game.view-model";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {GroupDomainModel} from "../../../domain/common/group/models/group.domain-model";
import {PartialGameViewModel} from "../../../domain/partial-game/models/partial-game-view-model";

export class StandingsWidgetRowViewModel extends BaseViewModel {
  competitor: StandingWidgetCompetitorViewModel;
  headers: Array<StandingsHeaderViewModel>;
  domainModel: StandingsRowDomainModel;
  datetimeUtils: DatetimeUtils;
  isHighlighted: Boolean;
  isRtl: Boolean;
  previousPosition: String;
  winnerTitle: String;

  constructor(
    domainModel: StandingsRowDomainModel,
    competitor: StandingWidgetCompetitorViewModel,
    headers: Array<StandingsHeaderViewModel>,
    datetimeUtils: DatetimeUtils,
    isHighlighted: Boolean,
    isRtl: Boolean,
    previousPosition: String,
    winnerTitle: String
  ) {
    super(domainModel);

    this.domainModel = domainModel;
    this.competitor = competitor;
    this.headers = headers;
    this.datetimeUtils = datetimeUtils;
    this.isHighlighted = isHighlighted;
    this.isRtl = isRtl;
    this.previousPosition = previousPosition;
    this.winnerTitle = winnerTitle;
    this.hasPointsDeduction = domainModel.hasPointsDeduction;
  }

  @computed get cells(): Array<StandingsWidgetCellViewModel> {
    let cells: Array<Object> = []
    let hasPointsCell = false;

    this.headers?.forEach((header: StandingsHeaderViewModel) => {
      let value = this.domainModel[header.key];
      const isLive: Boolean = header === ''
      if (isLive) {
        value = this.liveGame;
      } else if (header.isRecentForm) {
        value = this.domainModel.recentForm.map((recentFormObject: PartialGameDomainModel) => {
          if(recentFormObject instanceof PartialGameDomainModel) {
            const winnerStatus = recentFormObject.calcGameCompetitorStatus(this.domainModel.competitor.id);
            const date: String = this.datetimeUtils.toShortDate(recentFormObject.startTime);
            const time: String = this.datetimeUtils.toShortTime(recentFormObject.startTime);

            return new StandingsWidgetRecentFormGameViewModel(recentFormObject, date, time, winnerStatus);
          }
          else {
            return recentFormObject;
          }

        })
      } else if (header.isNextMatch && value) {
        const date: String = this.datetimeUtils.toShortDate(value?.startTime);
        const time: String = this.datetimeUtils.toShortTime(value?.startTime);
        value = new PartialGameViewModel(value, date, time);
      } else if (header.isDifferenceGoals) {
        const differenceGoalsRtl = `${this.domainModel.against}:${this.domainModel.for}`;
        const differenceGoalsLtr = `${this.domainModel.for}:${this.domainModel.against}`;
        value = this.isRtl ? differenceGoalsRtl : differenceGoalsLtr;
      }

      hasPointsCell |= [STANDINGS_WIDGET.PTS_KEY].includes(header.key);

      cells.push(new StandingsWidgetCellViewModel(value, header.key, header.isMajor, [STANDINGS_WIDGET.PTS_KEY].includes(header.key), header.isRecentForm, isLive));
    });

    //Last major cell should be bold
    const lastMajorCell: StandingsWidgetCellViewModel = cells?.filter((cell) => cell.isMajor)?.last();

    if (lastMajorCell && !hasPointsCell) {
      lastMajorCell.isBold = true;
    }

    return cells;
  }


  @computed get destination(): StandingsDestinationViewModel {
    if (!this.domainModel.destination) {
      return null;
    }

    const destination = new StandingsDestinationViewModel(this.domainModel.destination);

    if (this.isWinner) {
      destination.winnerText = this.winnerTerm;
    }

    return destination;
  }

  get group(): GroupDomainModel {
    return this.domainModel.group;
  }

  @computed get position(): Number {
    return this.domainModel.position;
  }

  @computed get trend(): STANDINGS_WIDGET.TREND {
    return this.domainModel.trend;
  }

  @computed get destinationGuaranteed(): Boolean {
    return this.domainModel.destinationGuaranteed;
  }

  @computed get isWinner(): Boolean {
    return this.domainModel.isWinner;
  }

  @computed get liveGame(): StandingWidgetLiveGameViewModel {
    const hasGame: Boolean = this.domainModel.game;
    const liveGame: StandingWidgetLiveGameViewModel = hasGame ? new StandingWidgetLiveGameViewModel(this.domainModel.game, this.domainModel.liveGameStatus) : null;

    return liveGame;
  }

  @computed get previousPositionTooltipLabel(): String {
    const previousPositionTooltipLabel: String = `${this.previousPositionTerm}: ${this.domainModel.previousPosition}`;

    return previousPositionTooltipLabel;
  }

  @computed get hasTrend(): Boolean {
    return this.trend !== TREND.even;
  }
}
